/**
 * Default locale-specific configurations
 * Single source of truth for all locale configs used in both SSR and CSR
 */

import { DEFAULT_LOCALE, toNormalisedLocale } from '@src/config/localisation/locale-config.constants'

import type { StatsigLocaleDynamicConfig } from './statsig-locale-config.types'

/** Statsig dynamic config key for locale config – single source of truth for SSR and CSR */
export const STATSIG_LOCALE_CONFIG_KEY = 'quince-locale-info'

/*
 * Zipcode config (maxLength, regex, format) – all locales, same shape:
 * | Locale | maxLength | regex (validation)           | format (space, uppercase)     |
 * | en-US  | 10        | 5 digits or 5-4               | no space, no uppercase         |
 * | en-CA  | 7         | A1A 1A1 (optional space)       | spaceBeforeLastN: 3, uppercase |
 * | fr-CA  | 7         | same as en-CA                 | spaceBeforeLastN: 3, uppercase |
 * | en-GB  | 8         | UK postcode (outcode+incode)  | spaceBeforeLastN: 3, uppercase |
 * Used by: maxLength → input + formatZipCodeInput; regex → validateZipCode/isValidZipCodeForLocale; format → formatZipCodeInput.
 */
/**
 * Default configuration for US locale (en-US)
 * Used as fallback when Statsig config is unavailable
 */
export const DEFAULT_US_LOCALE_CONFIG: StatsigLocaleDynamicConfig = {
  zipcode: {
    maxLength: 10,
    // eslint-disable-next-line i18next/no-literal-string
    regex: '(^\\d{5}$)|(^\\d{5}-\\d{4}$)',
    format: {
      hasSpace: false,
      spacePosition: '',
      spaceBeforeLastN: '',
      toUpperCase: false,
    },
  },
  // Phase 1: Price threshold for "Under $X Picks"
  priceThreshold: 30,
  // Phase 1: App push banner enabled
  appPushBannerEnabled: true,
  // Phase 2: Experiment & Feature Eligibility
  repeatableExperimentEnabled: true,
  siteDutyMessageEnabled: false,
  showDutiesInformation: false,
  bestSellerPersonalizedExpEnabled: true,
  swellRequiresToken: false,
  pdpModelSizeTagEnabled: true,
  showMarketingConsent: false, // Marketing consent checkbox disabled by default; enabled per region via Statsig
  currencyIcon: 'dollar', // US/CA use the dollar ($) currency-symbol icon
  // Phase 4: Language & UI Behavior

  yotpo: {
    requiresTranslationOverride: false,
    guid: {
      prod: 'Ll6pppZ6VQxK_x86N6mnnw',
      test: 'OHoPhD5PXj9uYDRxkLP7Qw',
    },
  },
  edd: {
    wrapping: {
      enabled: false,
      excludeDesktop: false,
    },
    eddDateFormat: 'month-first',
    cutoffWrap: false,
  },
  gifting: {
    tooltipVariant: 'default',
  },
  reviewSystemV3Enabled: true,
  newCartRecommendationsEnabled: true, // Enabled for US
  expressCheckoutEnabled: true, // Enabled for US
  showCurrencyCode: false, // US doesn't show currency code
  newTrendingSearchEndpoint: false, // Disabled for US — uses pre-fetched trendingSearchSuggestions
  pdpDesignV2Enabled: true, // Enabled for US
  mteRerankerEnabled: false, // US: MTE ranking model is driven by the per-user PDP_MTE_RERANKER experiment
  verifiedPurchaseBadgeEnabled: false, // Verified-purchase badge off by default; enable per locale via Statsig
  gladlyHelpCenter: {
    apiUrl: 'https://quince.us-1.gladly.com',
    orgId: 'Bq5O4FGeR6CHGQtaizG6gA',
    sdkUrl: 'https://cdn.gladly.com/help-center/hcl.js',
    cdn: 'https://cdn.gladly.com',
    brandId: 'quince.com-en-us',
  },
  gladlyChat: {
    defaultAppId: 'quince.com',
    corporateGiftingAppId: 'quince.com-corporate-gifting',
  },
  algolia: {
    appId: 'K81D2WQH9L',
    searchApiKey: '3f306f13f22cfa9128f18019ba17dc34',
  },
  klarnaClientId: 'b422da9d-78be-55a0-a693-7d1c18ca1b82',
  displayLocalisedReviewSize: false,
  skipProvinceInAddress: false, // US uses province/state field
  trustArcDomain: 'quince.com',
  trustArcCookieName: 'quince_us',
  consentBannerDisabledRoutes: [], // empty = consent banner shows on every route (US default)
  showItemizedTaxBreakdownInPriceSummary: false,
  showTotalExcludingVatInPriceSummary: false,
  showDownloadInvoiceOrder: false,
  hideTaxesCalculatedAtCheckoutMessage: false, // US keeps the "taxes calculated at checkout" note
  hideCartUrgencyMessage: false,
  // US is NOT subject to advertising-consent gating — 3P SDKs initialise on page load.
  countryCompliant: false,
  // US national number: 10 digits, no trunk prefix
  phone: { countryCode: '+1', trunkPrefix: '', regex: String.raw`^\d{10}$`, maxLength: 10 },
  showReferralHistory: true, // Referral history lists shown on My Rewards
  additionalFbPixelsAllowed: true, // US runs the Blotout/Forked secondary FB pixels
  isGa4Allowed: true, // US fires GA4
  isGoogleAdsAllowed: true, // US fires Google Ads / GTM server container
}

// Shared fallback for "Under $X Picks" (used when Statsig doesn't provide priceThreshold)
export const FALLBACK_PRICE_THRESHOLD = 30

/**
 * Default configuration for English Canadian locale (en-CA)
 */
export const DEFAULT_EN_CA_LOCALE_CONFIG: StatsigLocaleDynamicConfig = {
  zipcode: {
    maxLength: 7,
    // eslint-disable-next-line i18next/no-literal-string
    regex: '^[A-Za-z]\\d[A-Za-z] ?\\d[A-Za-z]\\d$',
    format: {
      hasSpace: true,
      spacePosition: 3,
      spaceBeforeLastN: '',
      toUpperCase: true,
    },
  },
  // Phase 1: Price threshold for "Under $X Picks" - CA uses $50
  priceThreshold: 50,
  // Phase 1: App push banner disabled for Canada
  appPushBannerEnabled: false,
  // Phase 2: Experiment & Feature Eligibility
  repeatableExperimentEnabled: false, // Disabled for Canada
  siteDutyMessageEnabled: true,
  showDutiesInformation: false,
  bestSellerPersonalizedExpEnabled: false, // Disabled for Canada
  swellRequiresToken: true, // CA requires token
  pdpModelSizeTagEnabled: false, // Disabled for Canada
  showMarketingConsent: false, // Marketing consent checkbox disabled by default; enabled per region via Statsig
  currencyIcon: 'dollar', // US/CA use the dollar ($) currency-symbol icon
  // Phase 4: Language & UI Behavior

  yotpo: {
    requiresTranslationOverride: false,
    guid: {
      prod: '7Z9O-0Ervm9Hh9vB1iT9nA',
      test: 'rfB4BfJ5UvqllD9LMAzGAg',
    },
  },
  edd: {
    wrapping: {
      enabled: true, // EDD wrapping enabled for en-CA
      excludeDesktop: true, // Desktop excluded for en-CA
    },
    eddDateFormat: 'month-first',
    cutoffWrap: false,
  },
  gifting: {
    tooltipVariant: 'default',
  },
  reviewSystemV3Enabled: false, // Disabled for Canada
  newCartRecommendationsEnabled: false, // Disabled for Canada
  expressCheckoutEnabled: false, // Disabled for Canada
  showCurrencyCode: true, // Canada shows currency code
  newTrendingSearchEndpoint: false,
  pdpDesignV2Enabled: false, // Disabled for Canada
  mteRerankerEnabled: false, // CA: MTE ranking model is driven by the per-user PDP_MTE_RERANKER experiment
  verifiedPurchaseBadgeEnabled: false, // Verified-purchase badge off by default; enable per locale via Statsig
  gladlyHelpCenter: {
    apiUrl: 'https://quince-ca.us-1.gladly.com',
    orgId: 'arEOWSMpTy6kDR7AFRPLQQ',
    sdkUrl: 'https://cdn.gladly.com/help-center/hcl.js',
    cdn: 'https://cdn.gladly.com',
    brandId: 'quince.ca-en-us',
  },
  gladlyChat: {
    defaultAppId: 'quince.ca',
    corporateGiftingAppId: 'quince.ca-corporate-gifting',
  },
  algolia: {
    appId: 'K81D2WQH9L',
    searchApiKey: '3f306f13f22cfa9128f18019ba17dc34',
  },
  klarnaClientId: 'de6c249a-249b-548b-80e8-ff6a227f8957',
  displayLocalisedReviewSize: false,
  skipProvinceInAddress: false, // Canada uses province field
  trustArcDomain: 'quince.com',
  trustArcCookieName: 'quince_ca',
  consentBannerDisabledRoutes: [], // empty = consent banner shows on every route (CA default)
  showItemizedTaxBreakdownInPriceSummary: false,
  showTotalExcludingVatInPriceSummary: false,
  showDownloadInvoiceOrder: false,
  hideTaxesCalculatedAtCheckoutMessage: false, // CA keeps the "taxes calculated at checkout" note
  hideCartUrgencyMessage: false,
  // en-CA is NOT subject to advertising-consent gating — 3P SDKs initialise on page load.
  countryCompliant: false,
  // CA national number: 10 digits, no trunk prefix
  phone: { countryCode: '+1', trunkPrefix: '', regex: String.raw`^\d{10}$`, maxLength: 10 },
  showReferralHistory: true, // Referral history lists shown on My Rewards
  additionalFbPixelsAllowed: true, // CA runs the Blotout/Forked secondary FB pixels
  isGa4Allowed: true, // CA fires GA4
  isGoogleAdsAllowed: true, // CA fires Google Ads / GTM server container
}

/**
 * Default configuration for French Canadian locale (fr-CA)
 */
export const DEFAULT_FR_CA_LOCALE_CONFIG: StatsigLocaleDynamicConfig = {
  zipcode: {
    maxLength: 7,
    // eslint-disable-next-line i18next/no-literal-string
    regex: '^[A-Za-z]\\d[A-Za-z] ?\\d[A-Za-z]\\d$',
    format: {
      hasSpace: true,
      spacePosition: 3,
      spaceBeforeLastN: '',
      toUpperCase: true,
    },
  },
  // Phase 1: Price threshold for "Under $X Picks" - CA uses $50
  priceThreshold: 50,
  // Phase 1: App push banner disabled for Canada
  appPushBannerEnabled: false,
  // Phase 2: Experiment & Feature Eligibility
  repeatableExperimentEnabled: false, // Disabled for Canada
  siteDutyMessageEnabled: true,
  showDutiesInformation: false,
  bestSellerPersonalizedExpEnabled: false, // Disabled for Canada
  swellRequiresToken: true, // CA requires token
  pdpModelSizeTagEnabled: false, // Disabled for Canada
  showMarketingConsent: false, // Marketing consent checkbox disabled by default; enabled per region via Statsig
  currencyIcon: 'dollar', // CA uses the dollar ($) currency-symbol icon
  // Phase 3: Language & UI Behavior

  yotpo: {
    requiresTranslationOverride: true, // French requires Yotpo translation override
    guid: {
      prod: '7Z9O-0Ervm9Hh9vB1iT9nA',
      test: 'rfB4BfJ5UvqllD9LMAzGAg',
    },
  },
  edd: {
    wrapping: {
      enabled: true, // EDD wrapping enabled for fr-CA
      excludeDesktop: false, // Desktop not excluded for fr-CA
    },
    eddDateFormat: 'month-first',
    cutoffWrap: true, // fr-CA uses cutoff wrap styling
  },
  gifting: {
    tooltipVariant: 'narrow', // Narrow tooltip for longer copy (e.g. French)
  },
  reviewSystemV3Enabled: false, // Disabled for Canada
  newCartRecommendationsEnabled: false, // Disabled for Canada
  expressCheckoutEnabled: false, // Disabled for Canada
  showCurrencyCode: true, // Canada shows currency code
  newTrendingSearchEndpoint: false,
  pdpDesignV2Enabled: false, // Disabled for Canada
  mteRerankerEnabled: false, // CA: MTE ranking model is driven by the per-user PDP_MTE_RERANKER experiment
  verifiedPurchaseBadgeEnabled: false, // Verified-purchase badge off by default; enable per locale via Statsig
  gladlyHelpCenter: {
    apiUrl: 'https://quince-ca.us-1.gladly.com',
    orgId: 'arEOWSMpTy6kDR7AFRPLQQ',
    sdkUrl: 'https://cdn.gladly.com/help-center/hcl.js',
    cdn: 'https://cdn.gladly.com',
    brandId: 'E--DC1taTVWDaqUaeU-a_Q',
  },
  gladlyChat: {
    defaultAppId: 'quince.ca-french',
    corporateGiftingAppId: 'quince.ca-french-corporate-gifting',
  },
  algolia: {
    appId: 'K81D2WQH9L',
    searchApiKey: '3f306f13f22cfa9128f18019ba17dc34',
  },
  klarnaClientId: 'de6c249a-249b-548b-80e8-ff6a227f8957',
  displayLocalisedReviewSize: false,
  skipProvinceInAddress: false, // Canada uses province field
  trustArcDomain: 'quince.com',
  trustArcCookieName: 'quince_ca',
  consentBannerDisabledRoutes: [], // empty = consent banner shows on every route (CA default)
  showItemizedTaxBreakdownInPriceSummary: false,
  showTotalExcludingVatInPriceSummary: false,
  showDownloadInvoiceOrder: false,
  hideTaxesCalculatedAtCheckoutMessage: false, // CA keeps the "taxes calculated at checkout" note
  hideCartUrgencyMessage: false,
  // fr-CA is NOT subject to advertising-consent gating — 3P SDKs initialise on page load.
  countryCompliant: false,
  // CA national number: 10 digits, no trunk prefix
  phone: { countryCode: '+1', trunkPrefix: '', regex: String.raw`^\d{10}$`, maxLength: 10 },
  showReferralHistory: true, // Referral history lists shown on My Rewards
  additionalFbPixelsAllowed: true, // fr-CA runs the Blotout/Forked secondary FB pixels
  isGa4Allowed: true, // fr-CA fires GA4
  isGoogleAdsAllowed: true, // fr-CA fires Google Ads / GTM server container
}

/**
 * Default configuration for UK locale (en-GB)
 * UK postcode: outcode (2–4 chars) + space + incode (3 chars). Space before last 3 chars so M1 1AA and SW1A 1AA both work.
 */
export const DEFAULT_EN_GB_LOCALE_CONFIG: StatsigLocaleDynamicConfig = {
  zipcode: {
    maxLength: 8,
    // UK postcode: 1–2 letters + digit + optional alphanumeric, optional space, digit + 2 letters (case-insensitive)
    // eslint-disable-next-line i18next/no-literal-string
    regex: '^[A-Za-z]{1,2}[0-9][A-Za-z0-9]?\\s*[0-9][A-Za-z]{2}$',
    format: {
      hasSpace: true,
      spacePosition: '',
      spaceBeforeLastN: 3,
      toUpperCase: true,
      // eslint-disable-next-line i18next/no-literal-string
      outcodeRegex: '^[A-Z]{1,2}[0-9][A-Z0-9]?$',
      // eslint-disable-next-line i18next/no-literal-string
      incodePrefixRegex: '^[0-9][A-Z]{0,2}$',
    },
  },
  priceThreshold: 50,
  appPushBannerEnabled: false,
  repeatableExperimentEnabled: false,
  siteDutyMessageEnabled: false,
  showDutiesInformation: true,
  bestSellerPersonalizedExpEnabled: false,
  swellRequiresToken: true,
  pdpModelSizeTagEnabled: false,
  showMarketingConsent: true, // Marketing consent checkbox enabled by default; enabled per region via Statsig
  currencyIcon: 'pound', // UK uses the pound (£) currency-symbol icon

  yotpo: {
    requiresTranslationOverride: false,
    guid: {
      prod: 'Ll6pppZ6VQxK_x86N6mnnw',
      test: 'OHoPhD5PXj9uYDRxkLP7Qw',
    },
    referralWidget: {
      enabled: true,
      loaderGuid: '6dlM4StpkLdL5YWFiPlNaQ',
      widgetInstanceId: '1329116',
    },
  },
  edd: {
    wrapping: {
      enabled: true,
      excludeDesktop: true,
    },
    eddDateFormat: 'day-first',
    cutoffWrap: false,
  },
  gifting: {
    tooltipVariant: 'default',
  },
  reviewSystemV3Enabled: false,
  newCartRecommendationsEnabled: false,
  expressCheckoutEnabled: false,
  showCurrencyCode: true,
  newTrendingSearchEndpoint: true,
  pdpDesignV2Enabled: false,
  // UK: force the MTE reranker for 100% of traffic regardless of consent. The
  // per-user PDP_MTE_RERANKER experiment can't reach non-consenting UK users, so
  // this user-agnostic flag drives the ranking model instead. Rollback = flip to
  // false in the Statsig `quince-locale-info` config (no deploy needed).
  mteRerankerEnabled: true,
  verifiedPurchaseBadgeEnabled: false, // Verified-purchase badge off by default; enable per locale via Statsig
  gladlyHelpCenter: {
    apiUrl: 'https://quince.us-1.gladly.com',
    orgId: 'Bq5O4FGeR6CHGQtaizG6gA',
    sdkUrl: 'https://cdn.gladly.com/help-center/hcl.js',
    cdn: 'https://cdn.gladly.com',
    brandId: 'bf0xae4dTEOpdLEMtuBy-g',
    rewardProgrammeLearnMoreQuery: 'a=Earn-Quince-Points---id--nsJ1AIfETgOb0Pmj_sgktQ&trafficSource=footer',
  },
  gladlyChat: {
    defaultAppId: 'quince.com-uk-en',
    corporateGiftingAppId: 'quince.com-corporate-gifting',
  },
  algolia: {
    appId: 'K2N9TXAULC',
    searchApiKey: '8a425a71edcd04c76a345212390806c8',
  },
  // UK Klarna Web SDK client ID (non-secret, tied to the UK account; also valid for
  // future EU sites). Presence of a non-empty client ID is what enables Klarna on the
  // PDP (productCTA gates on `!klarnaClientId`) and loads the Web SDK. NOTE: only
  // Quince.com (prod) is whitelisted by Klarna today — lower envs (qa/preview) need
  // their URLs whitelisted before the SDK/messaging will render there.
  klarnaClientId:
    'klarna_live_client_bm9yTUpkaEojaHY2Z0ckZ1BpP2dpRj8oQUhnblFZZVosNGQ0YWIxYzgtMGUxMy00ZTE1LWE2YmYtNzU1ZjlkYTIyMjEyLDEsUnlsaWx0a0RxNTR6azFIRlV2Y3pma2piUlQzOVM5UGtOOXlnVWlEUkJXVT0',
  displayLocalisedReviewSize: true,
  skipProvinceInAddress: true, // UK does not use province/county in addresses
  trustArcDomain: 'quinceuk.com',
  trustArcCookieName: 'quince_uk',
  consentBannerDisabledRoutes: ['/privacy-policy'], // hide the TrustArc auto-banner on the UK privacy-policy page
  showItemizedTaxBreakdownInPriceSummary: true,
  showTotalExcludingVatInPriceSummary: true,
  showDownloadInvoiceOrder: true,
  hideTaxesCalculatedAtCheckoutMessage: true,
  hideCartUrgencyMessage: true,
  // UK requires advertising consent before any 3P SDK can be initialised
  countryCompliant: true,
  // UK: trunk prefix '0' is presentation-only — the helpers add it on display/prefill
  // (E.164 +447825598628 → 07825598628) and strip it on submit (wire stays unchanged).
  // regex tests the national value after the country code + non-digits are stripped:
  // it requires the leading 0 (10–11 digits total). maxLength 11 caps the input.
  phone: { countryCode: '+44', trunkPrefix: '0', regex: String.raw`^0\d{9,10}$`, maxLength: 11 },
  // UK hides the referral history lists ("Successfully referred friends" / "Your referrals") on My Rewards
  showReferralHistory: false,
  // UK does NOT run the Blotout/Forked secondary FB pixels — only the primary
  // CONFIG_KEY_FB_PIXEL_ID fires here. These additional pixels are US/CA-only.
  additionalFbPixelsAllowed: false,
  // UK does NOT fire the GA4 tag or the Google Ads / GTM server container, and no
  // events route through them — blocked even after advertising consent until the
  // UK compliance review approves them.
  isGa4Allowed: false,
  isGoogleAdsAllowed: false,
}

/**
 * Default locale configuration map
 * Single source of truth for all locale-specific configurations
 */
export const DEFAULT_LOCALE_CONFIG_MAP = {
  'en-US': DEFAULT_US_LOCALE_CONFIG,
  'en-CA': DEFAULT_EN_CA_LOCALE_CONFIG,
  'fr-CA': DEFAULT_FR_CA_LOCALE_CONFIG,
  'en-GB': DEFAULT_EN_GB_LOCALE_CONFIG,
} as const

/**
 * Get locale configuration with smart fallback logic
 *
 * ⚠️ CRITICAL: This is ONLY used as a FALLBACK when Statsig fetch fails or statsig misses any key!
 *
 *
 *
 * @param locale - The absolute locale to look up
 * @returns Locale configuration with smart fallback
 */
export function getLocaleConfig(locale: string): StatsigLocaleDynamicConfig {
  // Normalise to consistent casing (language lower, country upper) for config key lookup
  const normalisedLocale = toNormalisedLocale(locale)

  // 1. Try exact match first (e.g., 'fr-CA' -> DEFAULT_FR_CA_LOCALE_CONFIG)
  if (normalisedLocale in DEFAULT_LOCALE_CONFIG_MAP) {
    return DEFAULT_LOCALE_CONFIG_MAP[normalisedLocale as keyof typeof DEFAULT_LOCALE_CONFIG_MAP]
  }

  // 2. Final fallback: app default locale (e.g., 'en-US')
  const defaultLocaleKey = toNormalisedLocale(DEFAULT_LOCALE)
  return DEFAULT_LOCALE_CONFIG_MAP[defaultLocaleKey as keyof typeof DEFAULT_LOCALE_CONFIG_MAP]
}
